import React, { useEffect, useState, useRef, Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import PublicNavbar from "../components/PublicNavbar";
import { BACKEND_BASE as CENTRALIZED_BACKEND_BASE, API_URL as CENTRALIZED_API } from "../lib/api";

// Below-the-fold content is code-split so it isn't part of the initial bundle.
// Combined with DeferredSection, each chunk only downloads when the user scrolls
// near it.
const HomeImageCarousel = lazy(() => import("../components/HomeImageCarousel"));
const FacilityCarousel = lazy(() => import("../components/FacilityCarousel"));
const FacebookPosts = lazy(() => import("../components/FacebookPosts"));
const Rates = lazy(() => import("../pages/Rates"));
const Location = lazy(() => import("../pages/Location"));
const CourtRules = lazy(() => import("../pages/CourtRules"));
const Shop = lazy(() => import("../pages/Shop"));

// Defers mounting of heavy, below-the-fold content until it is about to scroll
// into view. Keeps the section wrapper (and its anchor id) in the DOM so nav
// links still work, while avoiding the upfront cost of iframes/carousels/fetches.
const DeferredSection = ({ children, placeholderMinHeight = 400, rootMargin = "300px" }) => {
  const ref = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (show) return undefined;
    const el = ref.current;
    if (!el) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setShow(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShow(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [show, rootMargin]);

  return (
    <div ref={ref} style={show ? undefined : { minHeight: placeholderMinHeight }}>
      {show ? <Suspense fallback={null}>{children}</Suspense> : null}
    </div>
  );
};

const FALLBACK_GALLERY_ITEMS = [
  { src: `${process.env.PUBLIC_URL}/assets/2.png`, alt: "Players enjoying a match" },
  { src: `${process.env.PUBLIC_URL}/assets/3.png`, alt: "Outdoor pickleball courts" },
  { src: `${process.env.PUBLIC_URL}/assets/4.png`, alt: "Club members posing together" },
];

// Use centralized API config for consistent URL handling
const BACKEND_BASE = CENTRALIZED_BACKEND_BASE;
const API = CENTRALIZED_API;

/** Prefer absolute S3/CDN URLs from the API; fall back to GridFS or legacy paths. */
const resolveCmsImageUrl = (item, { apiPathPrefix = 'gallery' } = {}) => {
  const absolute = item?.image_url || item?.image;
  if (typeof absolute === 'string' && absolute.startsWith('http')) return absolute;
  if (item?.image_file_id) return `${API}/${apiPathPrefix}/images/${item.image_file_id}`;
  if (typeof absolute === 'string' && absolute) {
    return `${BACKEND_BASE}${absolute.startsWith('/') ? '' : '/'}${absolute}`;
  }
  return '';
};

const resolveGalleryThumbUrl = (item, fullSrc) => {
  const thumb = item?.thumbnail_url || item?.thumbnail;
  if (typeof thumb === 'string' && thumb.startsWith('http')) return thumb;
  return thumb || fullSrc;
};

// Fallback facility items
const FALLBACK_FACILITY_ITEMS = [
  { src: `${process.env.PUBLIC_URL}/assets/f3.jpg`, alt: "Camsur Pickleball facility" },
  { src: `${process.env.PUBLIC_URL}/assets/f4.jpg`, alt: "Camsur Pickleball facility" },
  { src: `${process.env.PUBLIC_URL}/assets/f5.jpg`, alt: "Camsur Pickleball facility" },
  { src: `${process.env.PUBLIC_URL}/assets/f6.jpg`, alt: "Camsur Pickleball facility" },
  { src: `${process.env.PUBLIC_URL}/assets/f7.jpg`, alt: "Camsur Pickleball facility" },
  { src: `${process.env.PUBLIC_URL}/assets/f8.jpg`, alt: "Camsur Pickleball facility" },
  { src: `${process.env.PUBLIC_URL}/assets/f9.jpg`, alt: "Camsur Pickleball facility" },
  { src: `${process.env.PUBLIC_URL}/assets/f10.jpg`, alt: "Camsur Pickleball facility" },
  { src: `${process.env.PUBLIC_URL}/assets/f11.jpg`, alt: "Camsur Pickleball facility" },
  { src: `${process.env.PUBLIC_URL}/assets/f12.jpg`, alt: "Camsur Pickleball facility" },
];

const LandingPage = () => {
  const [galleryItems, setGalleryItems] = useState(FALLBACK_GALLERY_ITEMS);
  const [facilityItems, setFacilityItems] = useState(FALLBACK_FACILITY_ITEMS);

  useEffect(() => {
    let cancelled = false;

    const fetchGallery = async () => {
      try {
        const response = await fetch(`${API}/gallery`);
        if (!response.ok) return;

        const data = await response.json();
        const mapped = Array.isArray(data)
          ? data
              .filter((item) => item?.image || item?.image_file_id)
              .filter((item) => item.category !== 'Facilities') // Exclude Facilities category from gallery
              .map((item) => {
                const imageSrc = resolveCmsImageUrl(item, { apiPathPrefix: 'gallery' });
                return {
                  src: imageSrc,
                  thumbSrc: resolveGalleryThumbUrl(item, imageSrc),
                  alt: item.title || item.category || "Gallery image",
                  date: item.date || null,
                };
              })
          : [];

        if (!cancelled && mapped.length > 0) {
          setGalleryItems(mapped);
        }
      } catch (error) {
        console.error("Failed to load gallery:", error);
      }
    };

    fetchGallery();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch facility items from gallery API (Facilities category)
  useEffect(() => {
    let cancelled = false;

    const fetchFacilities = async () => {
      try {
        const response = await fetch(`${API}/gallery?category=Facilities`);
        if (!response.ok) return;

        const data = await response.json();
        const mapped = Array.isArray(data)
          ? data
              .filter((item) => item?.image || item?.image_file_id)
              .map((item) => {
                const imageSrc = resolveCmsImageUrl(item, { apiPathPrefix: 'gallery' });
                return {
                  src: imageSrc,
                  hdSrc: imageSrc,
                  thumbSrc: resolveGalleryThumbUrl(item, imageSrc),
                  alt: item.title || "Camsur Pickleball facility",
                  title: item.title || "",
                  description: item.description || "",
                };
              })
          : [];

        if (!cancelled && mapped.length > 0) {
          setFacilityItems(mapped);
        }
      } catch (error) {
        console.error("Failed to load facilities:", error);
      }
    };

    fetchFacilities();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white text-gray-900 font-avenir">
      {/* Hero */}
      <header className="relative" id="home">
        <PublicNavbar />

        <div className="relative h-[400px] w-full overflow-hidden md:h-screen">
          {/* Single hero image (LCP). Phone + tablet portrait: left player only.
              At md+ portrait the image is 200% wide so the clipped viewport shows
              just the left half — no center seam. Tablet landscape / desktop: full pano. */}
          <img
            src="/newassets/bg.png"
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            decoding="async"
            className="absolute top-0 left-0 h-full w-full object-cover object-left md:portrait:w-[200%] md:portrait:max-w-none md:portrait:object-left md:landscape:w-full md:landscape:object-center"
          />

          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/10 px-4">
            <div className="flex flex-col items-center pt-16 md:pt-20">
              <img
                src="/newassets/Logo.png"
                alt="Camsur Pickleball Club"
                fetchPriority="high"
                decoding="async"
                width={480}
                height={480}
                className="mx-auto h-auto w-40 max-w-[75vw] drop-shadow-[0_0_30px_rgba(255,255,255,0.53)] sm:w-52 md:w-80 lg:w-[26rem] xl:w-[30rem]"
              />

              <img
                src="/newassets/THREE ICONS.png"
                alt="icons"
                decoding="async"
                width={288}
                height={96}
                className="pointer-events-none mx-auto mt-6 h-auto w-36 max-w-[55vw] sm:w-44 md:mt-10 md:w-64 lg:w-72"
              />

              <div className="group relative mt-6 md:mt-8">
                <Link
                  to="/book/venue_camsur"
                  aria-label="Book a court"
                  className="relative z-20 flex items-center justify-center overflow-hidden rounded-full bg-[#194e7f] px-8 py-3 font-avenir text-sm font-bold uppercase text-white shadow-lg transition hover:bg-[#1e5a93] sm:px-10 sm:py-3.5 sm:text-base md:px-14 md:py-5 md:text-2xl lg:px-16 lg:text-3xl"
                >
                  <span className="pointer-events-none absolute inset-0 z-10 translate-x-[-100%] bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:animate-shimmer" />
                  <span className="relative z-20 whitespace-nowrap drop-shadow-md">
                    Book A Court
                  </span>
                </Link>

                <span
                  aria-hidden="true"
                  className="absolute bottom-[-4px] left-1 right-1 z-0 h-7 rounded-b-full bg-[#c7e44a] sm:h-8 md:h-9"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Intro / Club Description (mirrored from PickleBall.vue) */}
      <section
        id="intro"
        className="relative overflow-hidden bg-gradient-to-r from-[#f48a15] via-[#f37e16] to-[#f48a15] py-16 md:py-24"
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 h-2 bg-[#c7e150]"
        />
        <div className="mx-auto max-w-5xl px-6 text-center">
          <p className="text-base leading-relaxed text-white md:text-2xl md:leading-relaxed">
            Pickleball Club is a vibrant and inclusive association dedicated to
            fostering community, competition, and a lifelong love for the sport of
            pickleball. Bringing together players of all ages and skill levels, the
            club provides a welcoming environment where members can learn, play, and
            grow both on and off the court.
          </p>
        </div>
      </section>

      {/* Upcoming Schedule */}
      {/* <section className="bg-white" id="events">
        <div className="w-full bg-[#f08c1a] py-3 text-center text-xl font-semibold text-white">Upcoming Schedule</div>
        <div className="mx-auto max-w-6xl px-4 pb-2 pt-6 sm:px-8">
          <div className="flex items-center justify-center gap-2 bg-white pb-6 pt-3">
            {[0, 1, 2].map((dot) => (
              <span
                key={dot}
                className={`h-2.5 w-2.5 rounded-full border border-[#f08c1a] ${dot === 1 ? 'bg-[#f08c1a]' : 'bg-white'}`}
              />
            ))}
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {scheduleItems.map((item, idx) => (
              <article
                key={`${item.title}-${idx}`}
                className="flex items-start gap-5 rounded-[34px] border-2 border-orange-400 bg-white px-6 py-7 shadow-sm"
              >
                <div className="flex shrink-0 flex-col overflow-hidden rounded-full bg-[#f08c1a] text-white">
                  <div className="px-4 pt-3 text-center text-2xl font-bold leading-none">{item.day}</div>
                  <div className="mx-3 mt-2 h-px bg-white/80" />
                  <div className="px-4 pb-3 text-center text-sm font-semibold leading-none">{item.month}</div>
                </div>
                <div className="min-w-0 pt-1">
                  <div className="text-sm font-semibold text-[#d26a00]">{item.time}</div>
                  <div className="mt-2 space-y-1 text-left text-[15px] font-semibold text-gray-900">
                    <p className="leading-snug">{item.title}</p>
                    {item.subtitle && <p className="leading-snug">{item.subtitle}</p>}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section> */}

      {/* News and Updates (Facebook Posts) — has its own sky-blue background */}
      <section id="whats-new">
        <DeferredSection placeholderMinHeight={600}>
          <FacebookPosts />
        </DeferredSection>
      </section>

      {/* Gallery — owns its own background, separate from What's New */}
      <section
        id="gallery"
        className="py-10 relative"
        style={{
          backgroundImage: "url('/newassets/White BG.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="mx-auto px-4 relative z-10">
          <DeferredSection placeholderMinHeight={500}>
            <HomeImageCarousel images={galleryItems} />
          </DeferredSection>
        </div>
      </section>



      {/* Our Facility */}
      <section className="bg-white py-12 relative" id="facility">
        <div className="mx-auto max-w-6xl px-4 relative z-10">
          <DeferredSection placeholderMinHeight={500}>
            <FacilityCarousel images={facilityItems} />
          </DeferredSection>
        </div>
        
      </section>

      <section id="rates">
        <DeferredSection placeholderMinHeight={700}>
          <Rates embedded={true} />
        </DeferredSection>
      </section>

      {/* Add-ons Section */}
      {/* <section className="bg-white py-14" id="addons">
        <div className="mx-auto max-w-6xl px-4">
          <h2 className="mb-8 text-center text-3xl font-bold text-[#f08c1a]">
            Add-ons
          </h2>

          <div className="mb-6 flex items-center justify-between">
            <h3 className="text-xl font-bold text-[#f08c1a]">
              Available Add-ons
            </h3>
          </div>

          <div className="relative flex items-center gap-4">
            <button
              onClick={() => slideAddon("prev")}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#f08c1a] text-2xl text-white shadow-lg transition duration-200 hover:bg-[#d26a00]"
              aria-label="Previous add-on"
            >
              ‹
            </button>

            <div className="flex-1 overflow-hidden">
              <div
                className="flex gap-4 transition-transform duration-500 ease-in-out py-4 px-4"
                style={{
                  transform: `translateX(-${
                    addonIndex * (100 / addonPerView)
                  }%)`,
                }}
              >
                {addonItems.map((item) => (
                  <div
                    key={item.item_id || item.name}
                    className="shrink-0"
                    style={{
                      width: `calc(${100 / addonPerView}% - ${
                        ((addonPerView - 1) * 16) / addonPerView
                      }px)`,
                    }}
                  >
                    <div className="rounded-3xl border-4 border-[#f08c1a] bg-white p-6 text-center shadow-lg transition-all duration-300 hover:bg-orange-50 hover:border-[#d26a00] hover:shadow-xl hover:scale-[1.02]">
                      <div className="mb-4 flex h-48 items-center justify-center overflow-hidden rounded-2xl bg-white">
                        <img
                          src={resolveCmsImageUrl(item, { apiPathPrefix: 'equipment' }) || item.image || 'https://images.pexels.com/photos/17299531/pexels-photo-17299531.jpeg'}
                          alt={item.name}
                          className="h-full w-full object-contain"
                          loading="lazy"
                          decoding="async"
                          onError={(e) => { e.target.src = 'https://images.pexels.com/photos/17299531/pexels-photo-17299531.jpeg'; }}
                        />
                      </div>
                      <h4 className="mb-3 text-sm font-bold text-gray-900">
                        {item.name}
                      </h4>
                      <p className="text-lg font-bold text-[#f08c1a]">
                        {typeof item.price === "number"
                          ? formatCurrency(item.price)
                          : item.price ||
                            formatCurrency(
                              item.price_per_hour || item.price_per_day || 0
                            )}
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-700">
                        {item.category === 'Services' ? 'Service' : `Available: ${Number(item.available_quantity ?? item.stock ?? 0)}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={() => slideAddon("next")}
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#f08c1a] text-2xl text-white shadow-lg transition duration-200 hover:bg-[#d26a00]"
              aria-label="Next add-on"
            >
              ›
            </button>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-sm font-bold">
            <button
              onClick={() => slideAddon("prev")}
              className="flex h-8 w-8 items-center justify-center rounded border-2 border-[#f08c1a] bg-white text-[#f08c1a] transition duration-200 hover:bg-[#f08c1a] hover:text-white"
            >
              ‹
            </button>
            {(() => {
              const totalPages = maxAddonIndex + 1;
              const currentPage = addonIndex + 1; // 1-based
              const pages = [];

              // Always show first page
              if (totalPages > 0) {
                pages.push(1);
              }

              // Calculate range around current page
              const start = Math.max(2, currentPage - 1);
              const end = Math.min(totalPages - 1, currentPage + 1);

              // Add ellipsis if there's a gap after first
              if (start > 2) {
                pages.push("...");
              }

              // Add pages around current
              for (let i = start; i <= end; i++) {
                if (!pages.includes(i)) {
                  pages.push(i);
                }
              }

              // Add ellipsis if there's a gap before last
              if (end < totalPages - 1) {
                pages.push("...");
              }

              // Always show last page if more than 1 page
              if (totalPages > 1 && !pages.includes(totalPages)) {
                pages.push(totalPages);
              }

              return pages.map((page, idx) => {
                if (page === "...") {
                  return (
                    <span
                      key={`ellipsis-${idx}`}
                      className="px-1 text-[#f08c1a]"
                    >
                      ...
                    </span>
                  );
                }
                const isActive = page === currentPage;
                return (
                  <button
                    key={page}
                    onClick={() => setAddonIndex(page - 1)}
                    className={`flex h-8 w-8 items-center justify-center rounded border-2 transition duration-200 ${
                      isActive
                        ? "border-[#f08c1a] bg-[#f08c1a] text-white"
                        : "border-[#f08c1a] bg-white text-[#f08c1a] hover:bg-[#f08c1a] hover:text-white"
                    }`}
                  >
                    {page}
                  </button>
                );
              });
            })()}
            <button
              onClick={() => slideAddon("next")}
              className="flex h-8 w-8 items-center justify-center rounded border-2 border-[#f08c1a] bg-white text-[#f08c1a] transition duration-200 hover:bg-[#f08c1a] hover:text-white"
            >
              ›
            </button>
          </div>
        </div>
      </section> */}
      
      <section id="addons">
        <DeferredSection placeholderMinHeight={700}>
          <Shop embedded={true} />
        </DeferredSection>
      </section>

      <section id="court-rules">
        <DeferredSection placeholderMinHeight={600}>
          <CourtRules embedded={true} />
        </DeferredSection>
      </section>

      <section id="location">
        <DeferredSection placeholderMinHeight={600}>
          <Location embedded={true} />
        </DeferredSection>
      </section>

      {/* Footer */}
      <footer 
        className="relative w-full text-white pt-16 overflow-hidden bg-cover bg-[90%_50%] lg:bg-[100%_50%]" 
        style={{ 
          backgroundImage: "url('/newassets/55.png')", 
          backgroundRepeat: 'no-repeat'
        }}
      >

        <div className="max-w-7xl mx-auto px-6 pb-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8 items-start">
            
            {/* Column 1: Logo */}
            <div className="flex justify-center md:justify-start">
              <img
                src="/assets/logo.png"
                alt="Camsur Pickleball Logo"
                className="h-40 md:h-56 w-auto drop-shadow-2xl"
              />
            </div>

            {/* Column 2: Navigation Links */}
            <div className="flex flex-col space-y-3 text-center md:text-left">
              {[
                { label: 'HOME', id: 'home' },
                { label: "WHAT'S NEW", id: 'whats-new' },
                { label: 'GALLERY', id: 'whats-new' },
                { label: 'FACILITY', id: 'facility' },
                { label: 'RATES', id: 'rates' },
                { label: 'SHOP', id: 'addons' },
                { label: 'COURT RULES', id: 'court-rules' },
                { label: 'LOCATION', id: 'location' }
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    const element = document.getElementById(item.id);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  className="font-bold text-sm hover:text-[#004a80] transition-colors text-left"
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Column 3: Membership & Socials */}
            <div className="space-y-8 text-center md:text-left">
              <div className="space-y-4">
                <h4 className="font-black text-lg">Membership</h4>
                <div className="flex flex-col space-y-3 font-bold text-sm">
                  <Link to="/login" className="hover:text-[#004a80] transition-colors">Login</Link>
                  {/* <Link  className="hover:text-[#004a80] transition-colors">Download Our App</Link> */}
                  <Link  className="hover:text-[#004a80] transition-colors">Privacy Policy</Link>
                </div>
              </div>
            </div>

            {/* Column 4: Contact & Legal */}
            <div className="space-y-6">
              <div>
                <h4 className="font-black text-lg text-center md:text-left mb-2">Legal</h4>
                <Link to="/login" className="block font-bold text-sm hover:text-[#004a80] text-center md:text-left">Member Login</Link>
              </div>
            </div>
          </div>
        </div>

        {/* Orange Copyright Bar */}
        <div className="bg-[#ff8a00] py-4 text-center text-white font-bold text-sm tracking-wider">
          2025 Camsur Pickleball. All Rights Reserved.
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;